import { forwardRef, Fragment } from 'react';
import {
  Box,
  Divider,
  Icon,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import type { ReactNode } from 'react';

export interface ThemedMenuItemDef {
  id: string;
  label: ReactNode;
  icon?: string;
  shortcut?: string;
  disabled?: boolean;
  selected?: boolean;
  /** Renders a divider before this item. */
  dividerBefore?: boolean;
  /** Prevents selection and shows a lock icon. */
  locked?: boolean;
  /** Tooltip shown on hover when the item is locked. */
  lockedTooltip?: string;
}

export interface ThemedMenuProps {
  anchorEl: Element | null;
  open: boolean;
  onClose: () => void;
  items: ThemedMenuItemDef[];
  onSelect?: (id: string) => void;
  dense?: boolean;
}

export const ThemedMenu = forwardRef<HTMLDivElement, ThemedMenuProps>(
  ({ anchorEl, open, onClose, items, onSelect, dense = false }, ref) => (
    <Menu
      ref={ref}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        list: { dense },
        paper: {
          elevation: 0,
          sx: {
            minWidth: 200,
            borderRadius: 0,
            boxShadow: 'none',
          },
        },
      }}
    >
      {items.map((item) => (
        <Fragment key={item.id}>
          {item.dividerBefore && <Divider />}
          <Tooltip
            title={item.locked ? (item.lockedTooltip ?? '') : ''}
            placement="bottom-end"
            disableHoverListener={!item.locked}
            disableFocusListener={!item.locked}
            disableTouchListener={!item.locked}
          >
            <MenuItem
              disabled={item.disabled}
              selected={item.selected}
              onClick={item.locked ? undefined : () => {
                onSelect?.(item.id);
                onClose();
              }}
              sx={{
                ...(item.locked && {
                  cursor: 'default',
                  '&:hover': { backgroundColor: 'transparent' },
                }),
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                  <Icon baseClassName="material-icons-outlined" sx={{ fontSize: 20 }}>
                    {item.icon}
                  </Icon>
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    sx: {
                      fontWeight: item.selected ? 500 : 400,
                      color: item.locked ? 'text.disabled' : undefined,
                    },
                  },
                }}
              />
              {item.locked && (
                <Icon
                  baseClassName="material-icons-outlined"
                  sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }}
                >
                  lock
                </Icon>
              )}
              {!item.locked && item.shortcut && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    color: (theme) => theme.palette.inflow.outline,
                    ml: 2,
                    flexShrink: 0,
                  }}
                >
                  {item.shortcut}
                </Box>
              )}
            </MenuItem>
          </Tooltip>
        </Fragment>
      ))}
    </Menu>
  ),
);

ThemedMenu.displayName = 'ThemedMenu';
